import { ChevronDown } from "lucide-react";
import { parseMedicalSummaryHtml } from "../utils/parseMedicalSummaryHtml";
import { MESSAGES } from "../constants/app";
import { InlineLoading } from "./loading/LoadingIndicators";

interface PatientAiOverviewPanelProps {
  summaryHtml: string;
  loading: boolean;
  error: string | null;
  isExpanded: boolean;
  onToggleExpanded?: () => void;
  contentId?: string;
  className?: string;
  contentOverflow?: "auto" | "visible";
}

type SummarySectionKey =
  | "clinicalSummary"
  | "riskSnapshot"
  | "keyConcern"
  | "watch";

const SECTIONS: { key: SummarySectionKey; label: string }[] = [
  { key: "clinicalSummary", label: "Clinical Summary" },
  { key: "riskSnapshot", label: "Risk Snapshot" },
  { key: "keyConcern", label: "Key Concern" },
  { key: "watch", label: "Watch" },
];

const SECTION_TITLE_CLASS =
  "mb-1 text-sm font-medium leading-5 tracking-[0.1px] text-guardian-body";

export default function PatientAiOverviewPanel({
  summaryHtml,
  loading,
  error,
  isExpanded,
  onToggleExpanded,
  contentId = "patient-ai-overview-content",
  className = "",
  contentOverflow = "auto",
}: PatientAiOverviewPanelProps) {
  const parsed = parseMedicalSummaryHtml(summaryHtml);
  const isScrollableContent = contentOverflow === "auto";
  const panelClassName = isScrollableContent
    ? "flex h-full min-h-0 flex-col overflow-hidden rounded-2xl bg-white"
    : "flex flex-col overflow-visible rounded-2xl bg-white";
  const contentClassName = isExpanded
    ? isScrollableContent
      ? "min-h-0 flex-1 overflow-y-auto"
      : "overflow-visible"
    : "hidden";
  const headerContent = (
    <>
      <span className="flex min-w-0 items-center gap-2">
        <img
          src="/images/ai_sparkle.png"
          alt=""
          className="h-6 w-6 shrink-0"
          aria-hidden
        />
        <span className="text-base font-extrabold tracking-tight text-guardian-body">
          AI Overview
        </span>
      </span>
      {onToggleExpanded && (
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-guardian-80 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      )}
    </>
  );

  return (
    <div className={`${panelClassName} ${className}`}>
      {onToggleExpanded ? (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          className="flex h-12 min-h-[48px] w-full shrink-0 items-center justify-between gap-3 border-b border-guardian-20 px-4 py-3 text-left transition-colors hover:bg-guardian-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-rag-green/40"
        >
          {headerContent}
        </button>
      ) : (
        <div className="flex h-12 min-h-[48px] shrink-0 items-center gap-2 border-b border-guardian-20 px-4 py-3">
          {headerContent}
        </div>
      )}

      <div
        id={contentId}
        className={contentClassName}
      >
        {loading ? (
          <div className="px-4 py-3">
            <InlineLoading message={MESSAGES.LOADING.SUMMARY} />
          </div>
        ) : error ? (
          <div className="px-4 py-3 text-sm text-rag-red">{error}</div>
        ) : parsed.hasStructuredSections ? (
          SECTIONS.map(({ key, label }) => {
            const content = parsed[key];
            if (!content) return null;
            return (
              <div
                key={key}
                className="border-b border-guardian-20 px-4 py-3 last:border-b-0"
              >
                <div className={SECTION_TITLE_CLASS}>{label}</div>
                <div
                  className="text-sm leading-5 text-guardian-header [&_span]:leading-5"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>
            );
          })
        ) : summaryHtml ? (
          <div className="border-b border-guardian-20 px-4 py-3">
            <div className={SECTION_TITLE_CLASS}>Summary</div>
            <div
              className="text-sm leading-5 text-guardian-header"
              dangerouslySetInnerHTML={{ __html: summaryHtml }}
            />
          </div>
        ) : (
          <div className="px-4 py-3 text-sm text-guardian-header">
            No summary available.
          </div>
        )}
      </div>
    </div>
  );
}
